import { useChat } from '@ai-sdk/react';
import type { CarePlan, Goal, ServiceRequest } from '@medplum/fhirtypes';
import { DefaultChatTransport } from 'ai';
import type { UIMessage } from 'ai';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { z } from 'zod';

import { useApiClient } from '@eluve/api-client-provider';
import { getEndpointUrl } from '@eluve/api-contract';
import {
  CarePlanInstancePreview,
  MemoizedMarkdownProse,
  loadCarePlanInstance,
} from '@eluve/care-plan-render';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Heading,
  Icon,
  LoadingIcon,
  Progress,
  Text,
  Textarea,
  cn,
  useToast,
} from '@eluve/components';
import { useMedplum } from '@eluve/medplum-client';
import {
  type CarePlanMatchCandidate,
  type PhasedInstanceSpec,
  coercePartialPlanInstanceSpec,
  diffInstanceSpec,
  phasedInstanceSpecSchema,
  planValidationIssueSchema,
} from '@eluve/plan-shapes';
import { useAssignedTenantIdFromParams } from '@eluve/session-helpers';

import { assertBatchSucceeded, buildAcceptBundle } from './care-plan-instance';
import { carePlanSummary } from './care-plan-meta';
import { messageRenderKey, messageViewPropsEqual } from './chatMessageMemo';

// The tool output shape (mirrors the backend present-plan-instance tool) —
// rebuilt from the shared plan-shapes schemas so the frontend doesn't import the
// backend mastra lib while still validating the emission against the real schema.
const planInstanceDraftSchema = z.object({
  // The Tailor is phased-only, so a draft emission is always a phased spec —
  // validate against the phased variant (the verbatim shapes never tailor).
  plan: phasedInstanceSpecSchema,
  valid: z.boolean(),
  issues: z.array(planValidationIssueSchema),
});

type PlanInstanceDraft = z.infer<typeof planInstanceDraftSchema>;

// An `output-available` tool part can briefly carry a partial output before the
// full schema has streamed in. Validate against the schema (mirrors the Plan
// Builder path's `isPlanDraft`) so only a complete draft becomes the canonical
// draft or renders the tool chip — a partial one would otherwise throw when we
// read `draft.issues.length` and take down the Care Plan tab.
function isPlanInstanceDraft(output: unknown): output is PlanInstanceDraft {
  return planInstanceDraftSchema.safeParse(output).success;
}

type ToolUIPart = {
  type: string;
  toolCallId: string;
  state:
    | 'input-streaming'
    | 'input-available'
    | 'output-available'
    | 'output-error';
  input?: unknown;
  output?: unknown;
};

// The four static suggestion chips from the mockup.
const SUGGESTIONS = [
  'Home + resistance band only',
  'Keep sessions under 20 min',
  'Start gently — halve initial dosing',
  'Shift start to next Monday',
];

export const CarePlanTab: React.FC<{
  baseUrl: string;
  appointmentId: string;
  carePlan: CarePlan;
  onBackToNote: () => void;
  // Fired after the draft changes underneath us (accept or switch) so the parent
  // re-fetches the CarePlan and the new body flows back down as the `carePlan` prop.
  onDraftChanged: () => void;
}> = ({ baseUrl, appointmentId, carePlan, onBackToNote, onDraftChanged }) => {
  const tenantId = useAssignedTenantIdFromParams();
  const apiClient = useApiClient();
  const toast = useToast();
  const { client: medplumClient } = useMedplum();

  const threadId = `care-plan-tailor:${appointmentId}`;
  const loadedThreadRef = useRef<string | null>(null);

  // The decoded seed (turn zero) + the draft's current children, loaded once.
  const [seed, setSeed] = useState<PhasedInstanceSpec | null>(null);
  const [children, setChildren] = useState<{
    goals: Goal[];
    serviceRequests: ServiceRequest[];
  }>({ goals: [], serviceRequests: [] });
  const [loading, setLoading] = useState(true);
  const [previewExpanded, setPreviewExpanded] = useState(true);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!medplumClient) {
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { seed, goals, serviceRequests } = await loadCarePlanInstance(
          medplumClient,
          carePlan,
        );
        if (!cancelled) {
          setSeed(seed);
          setChildren({ goals, serviceRequests });
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [medplumClient, carePlan]);

  const transport = useMemo(
    () =>
      new DefaultChatTransport({
        api: getEndpointUrl(baseUrl, 'carePlanTailor', {
          params: { tenantId },
        }),
        credentials: 'include',
        prepareSendMessagesRequest: ({ messages: chatMessages }) => {
          const last = chatMessages.at(-1);
          const text =
            last?.parts
              .filter(
                (p): p is { type: 'text'; text: string } => p.type === 'text',
              )
              .map((p) => p.text)
              .join('') ?? '';
          return { body: { text, appointmentId, threadId } };
        },
      }),
    [baseUrl, tenantId, appointmentId, threadId],
  );

  const { messages, sendMessage, status, setMessages, stop } = useChat({
    transport,
    experimental_throttle: 50,
  });
  const busy = status === 'submitted' || status === 'streaming';

  // Restore the (deterministic) conversation thread on mount — the living draft
  // is conversation state, so the latest tailoring survives a tab switch / refresh.
  useEffect(() => {
    if (loadedThreadRef.current === threadId) {
      return;
    }
    let cancelled = false;
    // Switching appointments reuses this component instance with a new threadId.
    // Drop the previous thread's messages up front so the draft memo / Accept
    // can't act on the prior appointment's plan while the new thread loads (or
    // when the new appointment has no saved thread at all).
    setMessages([]);
    (async () => {
      try {
        const result = await apiClient.mastra.getCarePlanTailorThread({
          params: { tenantId, threadId },
        });
        if (cancelled) {
          return;
        }
        if (result.status === 200) {
          setMessages(result.body.messages as UIMessage[]);
        }
      } catch {
        // No saved thread yet — start fresh.
      } finally {
        if (!cancelled) {
          loadedThreadRef.current = threadId;
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiClient, tenantId, threadId, setMessages]);

  // The living draft: the LATEST presentPlanInstance emission (full-replace).
  const draft = useMemo<PlanInstanceDraft | null>(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const message = messages[i];
      if (message?.role !== 'assistant') {
        continue;
      }
      for (let j = message.parts.length - 1; j >= 0; j--) {
        const part = message.parts[j] as ToolUIPart;
        if (
          part.type === 'tool-presentPlanInstance' &&
          part.state === 'output-available' &&
          isPlanInstanceDraft(part.output)
        ) {
          return part.output;
        }
      }
    }
    return null;
  }, [messages]);

  // The in-flight emission renders the draft building live as the tool input streams.
  const drafting = useMemo<PhasedInstanceSpec | null>(() => {
    if (!busy) {
      return null;
    }
    const last = messages.at(-1);
    if (last?.role !== 'assistant') {
      return null;
    }
    for (let j = last.parts.length - 1; j >= 0; j--) {
      const part = last.parts[j] as ToolUIPart;
      if (part.type !== 'tool-presentPlanInstance') {
        continue;
      }
      if (
        part.state !== 'input-streaming' &&
        part.state !== 'input-available'
      ) {
        return null;
      }
      return coercePartialPlanInstanceSpec(
        (part.input as { plan?: unknown } | undefined)?.plan,
      );
    }
    return null;
  }, [messages, busy]);

  // Keep the feed pinned to the latest turn as messages stream in.
  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: 'smooth',
    });
  }, [messages]);

  const settled = draft?.plan ?? seed;
  const spec = drafting ?? settled;
  const diff = useMemo(
    () => (seed && spec ? diffInstanceSpec(seed, spec) : null),
    [seed, spec],
  );

  const [accepting, setAccepting] = useState(false);
  const handleAccept = async () => {
    if (!medplumClient || !settled) {
      return;
    }
    setAccepting(true);
    try {
      const bundle = buildAcceptBundle({
        draftCarePlan: carePlan,
        existingGoals: children.goals,
        existingServiceRequests: children.serviceRequests,
        finalSpec: settled,
      });
      const result = await medplumClient.executeBatch(bundle);
      assertBatchSucceeded(result);
      medplumClient.invalidateSearches('CarePlan');
      medplumClient.invalidateSearches('ServiceRequest');
      medplumClient.invalidateSearches('Goal');
      toast.success('Care plan accepted');
      onDraftChanged();
    } catch (err) {
      toast.error(
        `Accept failed: ${err instanceof Error ? err.message : String(err)}`,
      );
    } finally {
      setAccepting(false);
    }
  };

  const summary = carePlanSummary(carePlan);
  const accepted = carePlan.status === 'active';
  const acceptDisabled =
    accepting || busy || (draft !== null && !draft.valid) || !settled;

  // The plan the draft is currently instantiating; the alternatives are every
  // OTHER candidate the matcher ranked (ENG-4764). Empty for pre-ENG-4764 drafts.
  const currentCanonical = carePlan.instantiatesCanonical?.[0];
  const alternatives = summary.candidates.filter(
    (c) => c.canonical !== currentCanonical,
  );

  // Canonical currently being switched-to (drives the row spinner), and the
  // pending switch awaiting confirmation when there's tailoring to discard.
  const [switching, setSwitching] = useState<string | null>(null);
  const [pendingSwitch, setPendingSwitch] = useState<{
    canonical: string;
    title: string;
  } | null>(null);

  const performSwitch = async (canonical: string, title: string) => {
    if (!medplumClient) {
      return;
    }
    // Abort any in-flight tailoring stream before re-seeding: a stream left
    // running would keep writing presentPlanInstance output for the OLD plan into
    // local state (and the soon-to-be-reset thread), which Accept could then apply
    // over the freshly switched draft. The Switch buttons are also disabled while
    // `busy`, so this is the belt to that suspenders.
    stop();
    setSwitching(canonical);
    try {
      const result = await apiClient.mastra.switchCarePlanDraft({
        params: { tenantId, appointmentId },
        body: { canonical },
      });
      if (result.status !== 200) {
        throw new Error('The plan could not be switched');
      }
      // The Tailor thread was reset server-side; drop the local conversation so
      // the fresh seed isn't overridden by the previous plan's tailoring.
      setMessages([]);
      medplumClient.invalidateSearches('CarePlan');
      medplumClient.invalidateSearches('ServiceRequest');
      medplumClient.invalidateSearches('Goal');
      toast.success(`Switched to ${title}`, {
        description: 'Started a fresh plan — prior tailoring was discarded.',
      });
      onDraftChanged();
    } catch (err) {
      toast.error(
        `Switch failed: ${err instanceof Error ? err.message : String(err)}`,
      );
    } finally {
      setSwitching(null);
    }
  };

  // Switching seeds a fresh draft and discards tailoring — confirm first only
  // when there's tailoring to lose; otherwise switch straight away.
  const requestSwitch = (canonical: string, title: string) => {
    if (draft !== null) {
      setPendingSwitch({ canonical, title });
    } else {
      void performSwitch(canonical, title);
    }
  };

  return (
    <div className="flex h-[calc(100vh-16rem)] min-h-96 flex-col">
      <div className="flex items-center justify-between gap-3 pb-3">
        <button
          type="button"
          onClick={onBackToNote}
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <Icon name="ArrowLeft" size="auto" className="size-4" />
          Back to Note
        </button>
        {!accepted ? (
          <Button onClick={handleAccept} disabled={acceptDisabled}>
            {accepting ? (
              <LoadingIcon />
            ) : (
              <Icon name="CheckCircle2" size="auto" />
            )}
            {diff?.hasChanges ? 'Accept tailored plan' : 'Accept plan'}
          </Button>
        ) : (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-bg-positive-subtle px-3 py-1 text-sm font-medium text-text-positive">
            <Icon name="CheckCircle2" size="auto" className="size-4" />
            Accepted
          </span>
        )}
      </div>

      <PreviewCard
        loading={loading || !seed || !spec}
        summary={summary}
        seed={seed}
        spec={spec}
        tailored={diff?.hasChanges ?? false}
        accepted={accepted}
        invalid={draft !== null && !draft.valid}
        expanded={previewExpanded}
        onToggle={() => setPreviewExpanded((v) => !v)}
        alternatives={accepted ? [] : alternatives}
        switching={switching}
        switchDisabled={busy}
        onSwitch={requestSwitch}
      />

      <div
        ref={scrollRef}
        className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto py-4"
      >
        {/* No empty-state callout — the composer placeholder is the prompt, so
            the feed stays empty (and scrollbar-free) until the chat starts. */}
        {messages.map((m) => (
          <MessageView
            key={m.id}
            message={m}
            busy={busy}
            renderKey={messageRenderKey(m)}
          />
        ))}
      </div>

      <div className="border-t border-border pt-3">
        <div className="mb-2 flex flex-wrap gap-1.5">
          {SUGGESTIONS.map((s) => (
            <button
              key={s}
              type="button"
              disabled={busy}
              onClick={() => sendMessage({ text: s })}
              className="rounded-full border border-border bg-card px-3 py-1 text-xs text-foreground transition-colors hover:bg-muted disabled:opacity-50"
            >
              {s}
            </button>
          ))}
        </div>
        <Composer
          onSubmit={(text) => sendMessage({ text })}
          onStop={stop}
          busy={busy}
          patientLabel="this patient"
        />
      </div>

      <AlertDialog
        open={pendingSwitch !== null}
        onOpenChange={(open) => {
          if (!open) {
            setPendingSwitch(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Switch to {pendingSwitch?.title}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This starts a fresh plan from the selected template. The tailoring
              you&apos;ve made to the current draft will be discarded.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep current plan</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pendingSwitch) {
                  void performSwitch(
                    pendingSwitch.canonical,
                    pendingSwitch.title,
                  );
                }
                setPendingSwitch(null);
              }}
            >
              Switch &amp; discard tailoring
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// The plan body, collapsed to a one-line summary header by default so the chat
// feed below is the primary surface. The match badge and Tailored/Accepted
// state stay visible even when collapsed; the full live-diffed preview lives in
// the (height-bounded, scrollable) body. The validation warning sits outside
// the collapse so it's always seen next to Accept.
// The ranked match panel (ENG-4764): one card that reads top-to-bottom as
// "Eluve weighed these and picked the best." The selected plan sits at the top
// with a strong match bar and a collapsible full-plan detail; the alternatives
// the matcher also considered sit in a compact "Also considered" callout right
// under the recommendation header — visible and switchable without scrolling —
// while the recommended plan's own goal/phases stay open by default (it's the
// useful context the clinician acts on, so it isn't hidden behind a toggle).
function PreviewCard({
  loading,
  summary,
  seed,
  spec,
  tailored,
  accepted,
  invalid,
  expanded,
  onToggle,
  alternatives,
  switching,
  switchDisabled,
  onSwitch,
}: {
  loading: boolean;
  summary: ReturnType<typeof carePlanSummary>;
  seed: PhasedInstanceSpec | null;
  spec: PhasedInstanceSpec | null;
  tailored: boolean;
  accepted: boolean;
  invalid: boolean;
  expanded: boolean;
  onToggle: () => void;
  alternatives: CarePlanMatchCandidate[];
  switching: string | null;
  // True while a tailoring stream is in flight — switching is blocked so an
  // active stream can't write the old plan over the freshly switched draft.
  switchDisabled: boolean;
  onSwitch: (canonical: string, title: string) => void;
}) {
  const meta = [
    'Phased',
    `${summary.phaseCount} phase${summary.phaseCount === 1 ? '' : 's'}`,
    summary.weeks ? `${summary.weeks} weeks` : null,
    'Practice library',
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="shrink-0 rounded-xl border border-border bg-card">
      <div className="flex items-center gap-2 px-4 pt-3">
        <Text
          size="xs"
          weight="medium"
          className="tracking-wider text-muted-foreground uppercase"
        >
          Recommended from this encounter
        </Text>
        <span className="ml-auto inline-flex items-center rounded-full border border-border px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
          {accepted ? 'Accepted' : tailored ? 'Tailored' : 'Verbatim template'}
        </span>
      </div>

      <div className="flex items-start gap-3 px-4 pt-2 pb-3">
        <span className="mt-0.5 grid size-4 shrink-0 place-items-center rounded-full bg-bg-positive-subtle">
          <Icon
            name="Check"
            size="auto"
            className="size-3 text-text-positive"
          />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1">
            <Heading size="lg" weight="semibold" level={2} className="min-w-0">
              {summary.title}
            </Heading>
            {summary.matchPct !== null ? (
              <MatchStrength pct={summary.matchPct} />
            ) : null}
          </div>
          <Text size="xs" color="secondary" className="mt-0.5">
            {meta}
          </Text>
        </div>
      </div>

      {alternatives.length > 0 ? (
        <div className="px-4 pb-3">
          <div className="rounded-lg bg-bg-secondary px-3 py-2.5">
            <Text
              size="xs"
              weight="medium"
              className="tracking-wider text-muted-foreground uppercase"
            >
              Also considered
            </Text>
            <div className="mt-2 space-y-2.5">
              {alternatives.map((alt) => (
                <div key={alt.canonical} className="flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-0.5">
                      <Text
                        size="sm"
                        weight="medium"
                        className="min-w-0 text-foreground"
                      >
                        {alt.title}
                      </Text>
                      <MatchStrength pct={Math.round(alt.confidence * 100)} />
                    </div>
                    {alt.rationale ? (
                      <Text size="xs" color="secondary" className="mt-0.5">
                        {alt.rationale}
                      </Text>
                    ) : null}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={switching !== null || switchDisabled}
                    onClick={() => onSwitch(alt.canonical, alt.title)}
                  >
                    {switching === alt.canonical ? (
                      <LoadingIcon />
                    ) : (
                      <Icon name="ArrowLeftRight" size="auto" />
                    )}
                    Switch
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : null}

      {invalid ? (
        <div className="px-4 pb-3">
          <Banner
            tone="warning"
            icon="AlertTriangle"
            text="Validation failed — Eluve will fix and re-emit before this can be accepted."
          />
        </div>
      ) : null}

      <div className="border-t border-border px-4 py-2">
        <button
          type="button"
          onClick={onToggle}
          className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
        >
          <Icon
            name={expanded ? 'ChevronUp' : 'ChevronDown'}
            size="auto"
            className="size-3.5"
          />
          {expanded ? 'Hide full plan' : 'View full plan'}
        </button>
      </div>

      {expanded ? (
        <div className="max-h-[40vh] overflow-y-auto px-4 pt-1 pb-3">
          {loading || !seed || !spec ? (
            <div className="flex items-center justify-center py-8">
              <LoadingIcon />
            </div>
          ) : (
            <div className="space-y-3">
              {summary.rationale.length > 0 ? (
                <ul className="space-y-1">
                  {summary.rationale.map((r, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-2 text-sm text-foreground"
                    >
                      <Icon
                        name="Check"
                        size="auto"
                        className="mt-0.5 size-3.5 shrink-0 text-text-positive"
                      />
                      <span>
                        <span className="font-semibold">{r.label}</span>
                        {r.text ? ` — ${r.text}` : ''}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : null}
              <CarePlanInstancePreview seed={seed} spec={spec} />
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

// Color the match readout by STRENGTH, not by which plan is selected, so a
// strong match draws the eye wherever it sits — including when the clinician has
// switched to a weaker plan and the 97% option is now an "also considered" row.
// Green ≥ 70, amber ≥ 40, red below.
function matchTone(pct: number): { text: string; indicator: string } {
  if (pct >= 70) {
    return {
      text: 'text-text-positive',
      indicator: 'bg-bg-positive-strong-primary',
    };
  }
  if (pct >= 40) {
    return {
      text: 'text-text-warning',
      indicator: 'bg-bg-warning-strong-primary',
    };
  }
  return {
    text: 'text-text-negative',
    indicator: 'bg-bg-negative-strong-primary',
  };
}

// The match-strength readout: "{pct}% match" + the shared Progress bar, both
// tinted by {@link matchTone}. A floor keeps a very low match (e.g. 4%) from
// rendering an invisible sliver.
function MatchStrength({ pct }: { pct: number }) {
  const tone = matchTone(pct);
  return (
    <div className="flex shrink-0 items-center gap-2">
      <span className={cn('text-xs font-semibold', tone.text)}>
        {pct}% match
      </span>
      <Progress
        value={Math.max(pct, 4)}
        className="h-1.5 w-16"
        indicatorClassName={tone.indicator}
      />
    </div>
  );
}

const MessageView = memo(function MessageView({
  message,
  busy,
}: {
  message: UIMessage;
  busy: boolean;
  // Immutable per-render signature, compared by `messageViewPropsEqual`. Not
  // read in the body — it exists solely so the memo can detect changes to the
  // in-place-mutated streaming message.
  renderKey: string;
}) {
  if (message.role === 'user') {
    const text = message.parts
      .filter((p): p is { type: 'text'; text: string } => p.type === 'text')
      .map((p) => p.text)
      .join('');
    if (text.length === 0) {
      return null;
    }
    return (
      <div className="flex flex-col items-end self-end">
        <div className="max-w-[85%] rounded-2xl rounded-br-sm bg-primary px-4 py-2 text-sm text-primary-foreground">
          {text}
        </div>
      </div>
    );
  }

  const hasVisible = message.parts.some(
    (p) =>
      (p.type === 'text' && p.text.length > 0) || p.type.startsWith('tool-'),
  );

  return (
    <div className="flex flex-col gap-2">
      {!hasVisible && busy ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Icon name="Sparkles" size="auto" className="size-4 shrink-0" />
          Thinking…
          <LoadingIcon size="auto" className="size-4 shrink-0" />
        </div>
      ) : null}
      {message.parts.map((part, i) => {
        if (part.type === 'text') {
          return part.text.length > 0 ? (
            <MemoizedMarkdownProse
              key={i}
              content={part.text}
              className="text-sm text-foreground"
            />
          ) : null;
        }
        if (part.type.startsWith('tool-')) {
          return <ToolPart key={i} part={part as unknown as ToolUIPart} />;
        }
        return null;
      })}
    </div>
  );
}, messageViewPropsEqual);

// The draft body renders in the preview above — the feed chip just marks each
// presentPlanInstance emission and its $validate verdict, ticking up with real
// counts from the partial spec while the tool input streams.
function ToolPart({ part }: { part: ToolUIPart }) {
  if (part.type !== 'tool-presentPlanInstance') {
    return null;
  }
  if (part.state === 'input-streaming' || part.state === 'input-available') {
    return (
      <DraftingStep
        stage={part.state}
        spec={coercePartialPlanInstanceSpec(
          (part.input as { plan?: unknown } | undefined)?.plan,
        )}
      />
    );
  }
  if (part.state !== 'output-available' || !isPlanInstanceDraft(part.output)) {
    return null;
  }
  const draft = part.output;
  return (
    <div className="inline-flex w-fit items-center gap-2 rounded-md border border-border bg-muted px-3 py-1.5 text-xs">
      <Icon
        name="Sparkles"
        size="auto"
        className="size-3.5 shrink-0 text-muted-foreground"
      />
      <span className="font-medium text-foreground">Draft updated</span>
      {draft.valid ? (
        <span className="text-text-positive-subtle">valid</span>
      ) : (
        <span className="text-text-warning-subtle">
          {draft.issues.length} issue{draft.issues.length === 1 ? '' : 's'}
        </span>
      )}
    </div>
  );
}

// Live progress for the streaming emission, from the coerced partial spec —
// real counts, not spinner theater.
function instanceProgress(spec: PhasedInstanceSpec | null): string | null {
  if (!spec) {
    return null;
  }
  const phaseCount = spec.phases?.length ?? 0;
  const activityCount =
    spec.phases?.reduce((sum, p) => sum + (p.activities?.length ?? 0), 0) ?? 0;
  const n = (count: number, noun: string) =>
    `${count} ${noun}${count === 1 ? '' : 's'}`;
  return (
    [
      phaseCount > 0 ? n(phaseCount, 'phase') : null,
      activityCount > 0 ? n(activityCount, 'activity') : null,
    ]
      .filter(Boolean)
      .join(' · ') || null
  );
}

function DraftingStep({
  stage,
  spec,
}: {
  stage: 'input-streaming' | 'input-available';
  spec: PhasedInstanceSpec | null;
}) {
  const progress = instanceProgress(spec);
  return (
    <div className="inline-flex w-fit max-w-full items-center gap-2 rounded-full bg-bg-strong-primary px-3 py-1.5 text-xs text-text-strong">
      <Icon name="Sparkles" size="auto" className="size-3.5 shrink-0" />
      <span className="font-medium">
        {stage === 'input-available'
          ? 'Validating with Medplum'
          : 'Tailoring plan'}
      </span>
      {progress ? (
        <Text inline size="xs" color="inherit" className="font-mono opacity-70">
          {progress}
        </Text>
      ) : null}
      <LoadingIcon size="auto" className="size-3.5 shrink-0" />
    </div>
  );
}

function Banner({
  tone,
  icon,
  text,
}: {
  tone: 'info' | 'warning';
  icon: 'Sparkles' | 'AlertTriangle';
  text: string;
}) {
  return (
    <div
      className={cn(
        'flex items-center gap-2 rounded-md px-3 py-2 text-xs',
        tone === 'info'
          ? 'bg-bg-strong-primary text-text-strong'
          : 'border border-border-warning-subtle bg-bg-warning-subtle text-text-warning',
      )}
    >
      <Icon
        name={icon}
        size="auto"
        className={cn('size-4 shrink-0', tone === 'info' && 'animate-pulse')}
      />
      {text}
    </div>
  );
}

function Composer({
  onSubmit,
  onStop,
  busy,
  patientLabel,
}: {
  onSubmit: (text: string) => void;
  onStop: () => void;
  busy: boolean;
  patientLabel: string;
}) {
  const ref = useRef<HTMLTextAreaElement | null>(null);
  const submit = () => {
    const text = ref.current?.value.trim() ?? '';
    if (text.length === 0 || busy) {
      return;
    }
    onSubmit(text);
    if (ref.current) {
      ref.current.value = '';
    }
  };
  return (
    <div className="flex items-end gap-2">
      <Textarea
        ref={ref}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            submit();
          }
        }}
        placeholder={`Tell Eluve AI what's different about ${patientLabel}…`}
        rows={1}
        className="max-h-40 min-h-11 flex-1 resize-none shadow-sm"
      />
      {busy ? (
        <Button onClick={onStop} variant="outline">
          <Icon name="Square" size="auto" className="fill-current" />
          Stop
        </Button>
      ) : (
        <Button onClick={submit}>
          <Icon name="Send" size="auto" />
          Send
        </Button>
      )}
    </div>
  );
}
